/**
 * Date Timezone Utilities for Quotation
 * 
 * Strict UTC flow:
 * - User enters date in local timezone
 * - Frontend converts local date to UTC ISO string before sending
 * - Backend receives only UTC
 * - Database stores UTC in timestamptz
 * - Backend fetches UTC
 * - Frontend converts UTC to local for display
 * - Filters convert local ranges to UTC before querying
 */

/**
 * Converts a local date string (YYYY-MM-DD) to UTC ISO string
 * Takes the local date at midnight and converts to UTC
 * @param localDateStr - Date string in YYYY-MM-DD format (local timezone)
 * @returns UTC ISO string (e.g., "2026-01-14T18:30:00.000Z") or empty string if invalid
 */
export const convertLocalDateToUTC = (localDateStr: string | undefined | null): string => {
  if (!localDateStr || localDateStr.trim() === "") return "";
  
  try {
    // Parse as local date (YYYY-MM-DD format)
    const [year, month, day] = localDateStr.split("-").map(Number);
    
    // Validate date components
    if (isNaN(year) || isNaN(month) || isNaN(day)) return "";
    if (month < 1 || month > 12 || day < 1 || day > 31) return "";
    
    // Create Date object in local timezone at midnight
    const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    
    // Validate the date
    if (isNaN(localDate.getTime())) return "";
    
    // Convert to UTC ISO string
    return localDate.toISOString();
  } catch (error) {
    console.error("Error converting local date to UTC:", error);
    return "";
  }
};

/**
 * Converts a UTC ISO string to local date string (YYYY-MM-DD)
 * For date-only fields (like follow-up dates), extracts the date part directly to avoid timezone shift
 * For datetime fields, converts to local timezone
 * @param utcDateStr - UTC ISO string (e.g., "2026-01-14T18:30:00.000Z")
 * @param preserveDateOnly - If true, extracts date part directly without timezone conversion (default: true)
 * @returns Local date string in YYYY-MM-DD format or empty string if invalid
 */
export const convertUTCToLocalDate = (utcDateStr: string | undefined | null, preserveDateOnly: boolean = true): string => {
  if (!utcDateStr || utcDateStr.trim() === "") return "";
  
  try {
    // If preserveDateOnly is true and it's an ISO string, extract date part directly
    // This prevents timezone conversion issues for date-only fields
    if (preserveDateOnly && (utcDateStr.includes("T") || utcDateStr.includes("Z"))) {
      // Extract date part from ISO string (YYYY-MM-DD)
      const datePart = utcDateStr.split("T")[0];
      
      // Validate the date part format
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    
    // For datetime fields or when preserveDateOnly is false, convert to local timezone
    const utcDate = new Date(utcDateStr);
    
    // Validate date
    if (isNaN(utcDate.getTime())) return "";
    
    // Get local date components
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, "0");
    const day = String(utcDate.getDate()).padStart(2, "0");
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error converting UTC to local date:", error);
    return "";
  }
};

/**
 * Converts a local date range to UTC range for filtering
 * @param fromDate - Local date string in YYYY-MM-DD format (start of day in local)
 * @param toDate - Local date string in YYYY-MM-DD format (end of day in local)
 * @returns Object with from and to as UTC ISO strings
 */
export const convertLocalDateRangeToUTC = (
  fromDate: string | undefined | null,
  toDate: string | undefined | null
): { from?: string; to?: string } => {
  const result: { from?: string; to?: string } = {};
  
  if (fromDate && fromDate.trim() !== "") {
    // Start of day in local timezone, converted to UTC
    const fromUTC = convertLocalDateToUTC(fromDate);
    if (fromUTC) {
      result.from = fromUTC;
    }
  }
  
  if (toDate && toDate.trim() !== "") {
    // End of day in local timezone, converted to UTC
    try {
      const [year, month, day] = toDate.split("-").map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        // Create Date object at end of day in local timezone (23:59:59.999)
        const localEndOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);
        if (!isNaN(localEndOfDay.getTime())) {
          result.to = localEndOfDay.toISOString();
        }
      }
    } catch (error) {
      console.error("Error converting to date to UTC:", error);
    }
  }
  
  return result;
};

/**
 * Converts a Date object to UTC ISO string
 * Useful when you have a Date object from date picker
 * @param date - Date object
 * @returns UTC ISO string or empty string if invalid
 */
export const convertDateToUTC = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return "";
  return date.toISOString();
};

/**
 * Converts a UTC ISO string to Date object in local timezone
 * Useful for date pickers
 * @param utcDateStr - UTC ISO string
 * @returns Date object in local timezone or null if invalid
 */
export const convertUTCToDate = (utcDateStr: string | undefined | null): Date | null => {
  if (!utcDateStr || utcDateStr.trim() === "") return null;
  
  try {
    const date = new Date(utcDateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch (error) {
    console.error("Error converting UTC to Date:", error);
    return null;
  }
};
